import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactNode } from 'react';
import { Button, Col, FormControl, FormLabel, FormSelect, InputGroup, Row, Table } from 'react-bootstrap';
import InputGroupText from 'react-bootstrap/esm/InputGroupText';
import AppEmptyState from './AppEmptyState';
import {
  useTable,
  useFilters,
  useGlobalFilter,
  usePagination,
  Column,
  UseGlobalFiltersInstanceProps,
  UseGlobalFiltersState,
  UsePaginationInstanceProps,
  UsePaginationState,
  TableInstance,
  TableState,
  Row as TableRow,
} from 'react-table';

interface IFilteredTableProps<T extends object> {
  columns: Array<Column<T>>;
  data: Array<T>;
  noDataMessage: string;
  noDataComponent?: ReactNode;
  tableBorderClass?: string;
  columnHeaderClass?: string;
  containerClass?: string;
  cellClass?: string;
  rowClass?: string;
  showAddButton?: boolean;
  addButtonText?: string;
  addButtonVariant?: string;
  addButtonHandler?: (event: any) => void;
  addButtonIcon?: IconDefinition;
  detailRow?: number | null;
  detailRowRender?: (row: TableRow<T>) => ReactNode;
  pageSize?: number; // Optional pageSize prop
  pageSizes?: Array<number>;
  showPagerTop?: boolean;
  showPagerBottom?: boolean;
  showFilter?: boolean;
  filterPlaceHolder?: string;
}

const TableFilter: React.FC<{
  globalFilter: string | undefined;
  setGlobalFilter: (filterValue: string | undefined) => void;
  placeHolder?: string;
}> = ({ globalFilter, setGlobalFilter, placeHolder }) => {
  return (
    <div className="app-filtered-table__filter">
      <InputGroup>
        <FormControl
          type="text"
          value={globalFilter || ""}
          onChange={(e) => setGlobalFilter(e.target.value || undefined)}
          placeholder={placeHolder ?? "filter..."}
        />
        <InputGroupText
          title="clear filter"
          onClick={() => setGlobalFilter(undefined)}
          style={{ cursor: "pointer" }}
          className="app-filtered-table__filter-clear"
        >
          <FontAwesomeIcon icon={faTimes} />
        </InputGroupText>
      </InputGroup>
    </div>
  );
};

const FilteredTable = <T extends object>(props: IFilteredTableProps<T>) => {
  const emptyMessage = props.noDataMessage.replace(/<[^>]*>/g, "").trim();

  const instance = useTable<T>(
    {
      columns: props.columns,
      data: props.data,
      initialState: { pageIndex: 0, pageSize: props.pageSize ?? 100 } as Partial<TableState<T> & UsePaginationState<T>>,
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    state,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
  } = instance as TableInstance<T> & UseGlobalFiltersInstanceProps<T> & UsePaginationInstanceProps<T>;

  const { globalFilter, pageIndex, pageSize } = state as TableState<T> & UseGlobalFiltersState<T> & UsePaginationState<T>;
  const defaultPageSizes = (sizes: Array<number>): Array<number> => {
    const pages: Array<number> = [];

    sizes.forEach(n => {
      if (n <= dataRows) {
        pages.push(n);
      }
    });

    return pages;
  };

  const dataRows = props.data?.length ?? 0;
  const pageSizes = defaultPageSizes(props.pageSizes ?? [10, 20, 30, 40, 50, 100]);
  const showPagerTop = props.showPagerTop ?? false;
  const showPagerBottom = props.showPagerBottom ?? false;
  const showFilter = props.showFilter ?? true;
  
  const tablePager = (): ReactNode => {
    return (
      <div className="app-filtered-table__pager mt-3">
        <div className="d-flex justify-content-center">
          <div className="d-flex align-items-center align-middle me-1">
            <Button variant="outline-secondary" className="btn-nomin me-1" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</Button>{' '}
            <Button variant="outline-secondary" className="btn-nomin me-1" onClick={() => previousPage()} disabled={!canPreviousPage}>{'<'}</Button>{' '}
            <Button variant="outline-secondary" className="btn-nomin me-1" onClick={() => nextPage()} disabled={!canNextPage}>{'>'}</Button>{' '}
            <Button variant="outline-secondary" className="btn-nomin me-1" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</Button>{' '}
          </div>
          <div className="d-flex align-items-center align-middle me-1">
            <span className="align-middle">
              Page{' '}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>{' '}
            </span>
          </div>
          <div className="d-flex align-items-center align-middle me-1">
            <FormLabel className="me-2 mb-0">&nbsp;|&nbsp;Go to page:&nbsp;</FormLabel>
            <FormControl
              type="number"
              min={1}
              max={pageOptions.length}
              step={1}
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              style={{ width: 'auto' }}
            />
          </div>
          <div className="d-flex align-items-center align-middle">
            <FormSelect
              hidden={dataRows < pageSize}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {dataRows > 0 && pageSizes.map((size) => (
                <option key={size} value={size}>Show {size}</option>
              ))}
              <option key={"all"} value={dataRows}>All {`(${dataRows})`}</option>
            </FormSelect>
          </div>
        </div>
      </div>
    )
  };

  return (
    <div>
      {(showFilter || props.showAddButton) && (
        <Row className="m-0 p-0 app-filtered-table__toolbar">
          <Col md={12} className="app-filtered-table__toolbar-inner">
          {props.showAddButton && (
            <Button
              className="me-2"
              variant={props.addButtonVariant ?? "success"}
              onClick={props.addButtonHandler ?? ((e) => console.log(e))}
            >
              <FontAwesomeIcon icon={props.addButtonIcon ?? faPlus} />&nbsp;
              {props.addButtonText ?? "Add new item"}
            </Button>
          )}
          {showFilter && props.data?.length > 0 && (
            <TableFilter
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              placeHolder={props.filterPlaceHolder}
            />
          )}
          </Col>
        </Row>
      )}

      {showPagerTop && (
        tablePager()
      )}

      {props.data && props.data.length > 0 ? (
        <div className={`table-responsive app-filtered-table ${showPagerBottom || showPagerTop ? "app-filtered-table--paged" : ""} ${props.containerClass ?? ""}`}>
          <Table {...getTableProps()} striped hover className="border mb-0 app-filtered-table__table">
            <thead>
              {headerGroups.map((headerGroup) => {
                const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                return (
                  <tr key={key} {...headerGroupProps}>
                    {headerGroup.headers.map((column) => {
                      const { key, ...columnProps } = column.getHeaderProps();
                      const headerClassName = column.id === 'special'
                        ? "app-filtered-table__header-cell app-filtered-table__header-cell--special"
                        : "app-filtered-table__header-cell align-middle";

                      return (
                        <th key={key} {...columnProps} className={headerClassName}>
                          {column.render("Header")}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>

            <tbody {...getTableBodyProps()}>
              {page.map((row, rowIndex) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <React.Fragment key={rowKey}>
                    <tr {...rowProps} className={props.rowClass}>
                      {row.cells.map((cell, cellIndex) => {
                        // Construct a unique key for each cell using row and column identifiers
                        const cellKey = `row-${rowIndex}-col-${cell.column.id || cellIndex}`;
                        const { key: cellGeneratedKey, ...cellProps } = cell.getCellProps();
                        return (
                          <td key={cellKey} {...cellProps} className={props.cellClass || "align-middle text-capitalize"}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>

                    {props.detailRow === row.index && props.detailRowRender && (
                      <tr key={`expanded-${rowKey}`}>
                        <td key={`expanded-row-${rowKey}`} colSpan={props.columns.length}>{props.detailRowRender(row)}</td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>

          </Table>

          {showPagerBottom && (
            tablePager()
          )}

        </div>
      ) : (
        <div className="m-2">
          {props.noDataComponent ?? <AppEmptyState
            compact
            className="app-filtered-table__empty"
            title="No items found"
            message={emptyMessage}
          />}
        </div>
      )}
    </div>
  );
};

export default FilteredTable;
